import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiSearch } from 'react-icons/fi';
import { 
  FaMapMarkerAlt, 
  FaUser, 
  FaHeart, 
  FaChevronDown, 
  FaComments, 
  FaList,
  FaPlus,
  FaSignOutAlt
} from 'react-icons/fa';
import './Navbar.css';
import { apiUrl } from '../api';
import { getFCMToken, sendTokenToBackend, onForegroundMessage } from '../firebase';

const locationOptions = [
  'Mumbai', 'Delhi', 'Bengaluru', 'Hyderabad', 'Chennai', 
  'Kolkata', 'Pune', 'Patna', 'Ranchi', 'Jaipur', 'Ahmedabad'
];

const locationLookup = {
  'Mumbai': { city: 'Mumbai' },
  'Delhi': { city: 'Delhi' },
  'Bengaluru': { city: 'Bengaluru' },
  'Hyderabad': { city: 'Hyderabad' },
  'Chennai': { city: 'Chennai' },
  'Kolkata': { city: 'Kolkata' },
  'Pune': { city: 'Pune' },
  'Bandra': { city: 'Mumbai', locality: 'Bandra' },
  'Andheri': { city: 'Mumbai', locality: 'Andheri' },
  'Connaught Place': { city: 'Delhi', locality: 'Connaught Place' },
  'Hauz Khas': { city: 'Delhi', locality: 'Hauz Khas' },
  'Indiranagar': { city: 'Bengaluru', locality: 'Indiranagar' },
  'Koramangala': { city: 'Bengaluru', locality: 'Koramangala' },
  'Hitec City': { city: 'Hyderabad', locality: 'Hitec City' },
  'T. Nagar': { city: 'Chennai', locality: 'T. Nagar' },
  'Park Street': { city: 'Kolkata', locality: 'Park Street' },
  'Koregaon Park': { city: 'Pune', locality: 'Koregaon Park' }
};

function Navbar({ token, onLogout }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchData, setSearchData] = useState({ product: '', location: '' });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isMobileLocDropdownOpen, setIsMobileLocDropdownOpen] = useState(false);

  const profileDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const mobileLocDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close overlays on navigation
  useEffect(() => {
    setIsMenuOpen(false);
    setIsProfileDropdownOpen(false);
    setIsMobileSearchOpen(false);
    setIsLocationDropdownOpen(false);
    setIsMobileLocDropdownOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer or search modal is open
  useEffect(() => {
    if (isMenuOpen || isMobileSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, isMobileSearchOpen]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(e.target)) {
        setIsProfileDropdownOpen(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(e.target)) {
        setIsLocationDropdownOpen(false);
      }
      if (mobileLocDropdownRef.current && !mobileLocDropdownRef.current.contains(e.target)) {
        setIsMobileLocDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/messages/unread/count'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const responseText = await response.text();
      if (response.ok) {
        try {
          const data = JSON.parse(responseText);
          setUnreadCount(data.unreadCount || 0);
        } catch (parseError) {
          console.error('Failed to parse unread count response:', responseText);
        }
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 10000);
      return () => clearInterval(interval);
    }
  }, [token, fetchUnreadCount]);

  // Firebase initialization
  useEffect(() => {
    if (!token) return;

    const initializeFirebase = async () => {
      try {
        if ('serviceWorker' in navigator) {
          try {
            await navigator.serviceWorker.register('/firebase-messaging-sw.js');
          } catch (err) {
            console.warn('Service Worker registration skipped/failed:', err);
          }
        }

        const fcmToken = await getFCMToken();
        if (fcmToken) {
          await sendTokenToBackend(fcmToken, token);
          localStorage.setItem('fcmToken', fcmToken);
        }

        const unsubscribe = onForegroundMessage((payload) => {
          fetchUnreadCount();
          if (Notification.permission === 'granted') {
            new Notification(
              payload.notification?.title || 'New Message from SaleKiya',
              {
                body: payload.notification?.body || 'You have a new message',
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: 'salekiya-message'
              }
            );
          }
        });

        return () => {
          if (typeof unsubscribe === 'function') unsubscribe();
        };
      } catch (error) {
        console.error('Firebase initialization error:', error);
      }
    };

    initializeFirebase();
  }, [token, fetchUnreadCount]);

  const handleLogout = () => {
    onLogout();
    localStorage.removeItem('userId');
    localStorage.removeItem('fcmToken');
    setIsProfileDropdownOpen(false);
    setIsMenuOpen(false);
    window.location.href = '/';
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectLocation = (loc, isMobile = false) => {
    setSearchData((prev) => ({ ...prev, location: loc }));
    if (isMobile) {
      setIsMobileLocDropdownOpen(false);
    } else {
      setIsLocationDropdownOpen(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    const keyword = searchData.product.trim();
    const locationValue = searchData.location.trim();

    if (keyword) params.append('keyword', keyword);
    if (locationValue) {
      const matched = locationLookup[locationValue] || { city: locationValue };
      if (matched.city) params.append('city', matched.city);
      if (matched.locality) params.append('locality', matched.locality);
    }

    setIsMobileSearchOpen(false);
    navigate(`/?${params.toString()}`);
  };

  const filteredLocations = locationOptions.filter((loc) =>
    loc.toLowerCase().includes(searchData.location.toLowerCase())
  );

  return (
    <>
      <header className="navbar">
        <div className="navbar-container">
          {/* Brand Logo */}
          <Link to="/" className="navbar-logo" aria-label="SaleKiya Home">
            Sale<span>Kiya</span>
          </Link>

          {/* Desktop Search */}
          <form className="navbar-search" onSubmit={handleSearchSubmit}>
            <div className="navbar-search-desktop">
              <div className="search-input-group">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  name="product"
                  placeholder="Find Cars, Mobile Phones, Electronics..."
                  value={searchData.product}
                  onChange={handleSearchChange}
                  className="navbar-search-input"
                />
              </div>

              <div className="search-divider" />

              <div className="navbar-location-wrapper" ref={locationDropdownRef}>
                <FaMapMarkerAlt className="navbar-location-icon" />
                <input
                  type="text"
                  name="location"
                  placeholder="All India"
                  value={searchData.location}
                  onChange={handleSearchChange}
                  onFocus={() => setIsLocationDropdownOpen(true)}
                  className="navbar-search-input navbar-location-input"
                  autoComplete="off"
                />
                {searchData.location && (
                  <button
                    type="button"
                    className="clear-location-btn"
                    onClick={() => handleSelectLocation('')}
                    aria-label="Clear location"
                  >
                    ×
                  </button>
                )}

                {/* Custom Location Dropdown Menu */}
                {isLocationDropdownOpen && (
                  <div className="custom-location-menu">
                    <div className="location-menu-header">Popular Locations</div>
                    <div className="location-menu-list">
                      {filteredLocations.map((option) => (
                        <button
                          type="button"
                          key={option}
                          className={`location-option-item ${searchData.location.toLowerCase() === option.toLowerCase() ? 'selected' : ''}`}
                          onClick={() => handleSelectLocation(option)}
                        >
                          <FaMapMarkerAlt className="option-marker-icon" />
                          <span>{option}</span>
                        </button>
                      ))}
                      {filteredLocations.length === 0 && (
                        <div className="no-location-found">No matching location</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" className="navbar-search-btn" aria-label="Search">
                <span>Search</span>
              </button>
            </div>
          </form>

          {/* Desktop Right Navigation */}
          <nav className="nav-menu-desktop" aria-label="Main navigation">
            {token ? (
              <>
                <Link to="/messages" className="nav-icon-link" title="Messages">
                  <FaComments />
                  {unreadCount > 0 && <span className="nav-badge">{unreadCount}</span>}
                </Link>

                <Link to="/wishlist" className="nav-icon-link" title="Wishlist">
                  <FaHeart />
                </Link>

                <div className="profile-dropdown" ref={profileDropdownRef}>
                  <button
                    className={`profile-toggle ${isProfileDropdownOpen ? 'active' : ''}`}
                    onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                    aria-label="User menu"
                    aria-expanded={isProfileDropdownOpen}
                  >
                    <div className="avatar-badge">
                      <FaUser />
                    </div>
                    <FaChevronDown className={`chevron-icon ${isProfileDropdownOpen ? 'rotated' : ''}`} />
                  </button>

                  {isProfileDropdownOpen && (
                    <div className="profile-dropdown-menu">
                      <div className="dropdown-header">
                        <span className="dropdown-title">Account</span>
                      </div>

                      <Link to="/profile" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaUser className="dropdown-icon" />
                        <span>My Profile</span>
                      </Link>

                      <Link to="/my-listings" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaList className="dropdown-icon" />
                        <span>My Listings</span>
                      </Link>

                      <Link to="/messages" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <div className="dropdown-item-left">
                          <FaComments className="dropdown-icon" />
                          {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                        </div>
                        <span>Messages</span>
                      </Link>

                      <Link to="/wishlist" className="dropdown-item" onClick={() => setIsProfileDropdownOpen(false)}>
                        <FaHeart className="dropdown-icon" />
                        <span>Wishlist</span>
                      </Link>

                      <div className="dropdown-divider" />

                      <button onClick={handleLogout} className="dropdown-item logout-item">
                        <FaSignOutAlt className="dropdown-icon" />
                        <span>Log Out</span>
                      </button>
                    </div>
                  )}
                </div>

                <Link to="/post" className="post-ad-btn">
                  <FaPlus className="post-plus-icon" />
                  <span>Post Ad</span>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="login-link">
                  <FaUser className="nav-icon" />
                  <span>Login</span>
                </Link>

                <Link to="/login" className="post-ad-btn">
                  <FaPlus className="post-plus-icon" />
                  <span>Post Ad</span>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile Right Actions: Search Trigger & Burger Button Side-by-Side */}
          <div className="mobile-actions-wrapper">
            <button
              type="button"
              className="navbar-search-mobile-trigger"
              onClick={() => setIsMobileSearchOpen(true)}
              aria-label="Open search dialog"
            >
              <FiSearch />
            </button>
            <button
              className="burger-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle navigation drawer"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <FiX /> : <FiMenu />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Drawer Navigation */}
      <div 
        className={`drawer-backdrop ${isMenuOpen ? 'active' : ''}`} 
        onClick={() => setIsMenuOpen(false)} 
      />
      <aside className={`mobile-drawer ${isMenuOpen ? 'open' : ''}`} aria-hidden={!isMenuOpen}>
        <div className="drawer-header">
          <Link to="/" className="navbar-logo" onClick={() => setIsMenuOpen(false)}>
            Sale<span>Kiya</span>
          </Link>
          <button className="drawer-close-btn" onClick={() => setIsMenuOpen(false)} aria-label="Close menu">
            <FiX />
          </button>
        </div>

        <div className="drawer-body">
          {token ? (
            <>
              <div className="drawer-user-card">
                <div className="avatar-badge large">
                  <FaUser />
                </div>
                <div className="user-details">
                  <span className="user-greeting">Signed In</span>
                  <Link to="/profile" className="user-sublink" onClick={() => setIsMenuOpen(false)}>
                    View Profile →
                  </Link>
                </div>
              </div>

              <Link to="/post" className="drawer-post-btn" onClick={() => setIsMenuOpen(false)}>
                <FaPlus /> <span>Post An Ad</span>
              </Link>

              <div className="drawer-nav-list">
                <span className="drawer-section-label">Manage Activity</span>
                <Link to="/my-listings" className="drawer-link" onClick={() => setIsMenuOpen(false)}>
                  <FaList className="drawer-icon" />
                  <span>My Listings</span>
                </Link>

                <Link to="/messages" className="drawer-link" onClick={() => setIsMenuOpen(false)}>
                  <div className="drawer-link-left">
                    <FaComments className="drawer-icon" />
                    {unreadCount > 0 && <span className="unread-badge">{unreadCount}</span>}
                  </div>
                  <span>Messages</span>
                </Link>

                <Link to="/wishlist" className="drawer-link" onClick={() => setIsMenuOpen(false)}>
                  <FaHeart className="drawer-icon" />
                  <span>Wishlist</span>
                </Link>
              </div>

              <div className="drawer-footer">
                <button onClick={handleLogout} className="drawer-logout-btn">
                  <FaSignOutAlt />
                  <span>Log Out</span>
                </button>
              </div>
            </>
          ) : (
            <div className="drawer-auth-box">
              <p className="auth-box-title">Buy, Sell and Connect</p>
              <p className="auth-box-desc">Log in to view chats, save favorites, and list products in seconds.</p>
              <Link to="/login" className="drawer-login-btn" onClick={() => setIsMenuOpen(false)}>
                Sign In / Register
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Search Modal */}
      {isMobileSearchOpen && (
        <div className="mobile-search-overlay" onClick={() => setIsMobileSearchOpen(false)}>
          <div className="mobile-search-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-search-header">
              <h3>Search Marketplace</h3>
              <button
                type="button"
                className="mobile-search-close"
                onClick={() => setIsMobileSearchOpen(false)}
                aria-label="Close search"
              >
                <FiX />
              </button>
            </div>

            <form className="mobile-search-form" onSubmit={handleSearchSubmit}>
              <div className="mobile-search-field">
                <FiSearch className="mobile-field-icon" />
                <input
                  type="text"
                  name="product"
                  placeholder="What are you looking for?"
                  value={searchData.product}
                  onChange={handleSearchChange}
                  className="mobile-search-input"
                  autoFocus
                />
              </div>

              <div className="mobile-search-field" ref={mobileLocDropdownRef}>
                <FaMapMarkerAlt className="mobile-field-icon location" />
                <input
                  type="text"
                  name="location"
                  placeholder="All India or City"
                  value={searchData.location}
                  onChange={handleSearchChange}
                  onFocus={() => setIsMobileLocDropdownOpen(true)}
                  className="mobile-search-input"
                  autoComplete="off"
                />
                {searchData.location && (
                  <button
                    type="button"
                    className="clear-location-btn mobile"
                    onClick={() => handleSelectLocation('', true)}
                    aria-label="Clear location"
                  >
                    ×
                  </button>
                )}

                {/* Mobile Dropdown Options */}
                {isMobileLocDropdownOpen && (
                  <div className="custom-location-menu mobile-version">
                    <div className="location-menu-header">Popular Locations</div>
                    <div className="location-menu-list">
                      {filteredLocations.map((option) => (
                        <button
                          type="button"
                          key={option}
                          className={`location-option-item ${searchData.location.toLowerCase() === option.toLowerCase() ? 'selected' : ''}`}
                          onClick={() => handleSelectLocation(option, true)}
                        >
                          <FaMapMarkerAlt className="option-marker-icon" />
                          <span>{option}</span>
                        </button>
                      ))}
                      {filteredLocations.length === 0 && (
                        <div className="no-location-found">No matching location</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button type="submit" className="mobile-search-submit">
                Search Now
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Navbar;